import React, { useCallback, useEffect, useRef, useState } from "react";
import { TaskRepetitionType, TaskVisibility } from "ui/Scripts/source/models/enums";
import { ITaskDataRow, SaveTaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { Box } from "@mui/material";
import { SaveTaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { SaveDataObj } from "source/framework/saveData";
import { AppUtils } from "source/framework/appUtils";
import { Base } from "ui/Scripts/source/framework/base";
import { Translations } from "ui/Scripts/source/models/translations";
import { PrintButton } from "source/components/framework/Button/PrintButton";
import { TaskTypePrintHtml } from "source/models/taskType/taskTypePrintHtml";

interface ITaskTypeDialogPreviewPrintProps {
    height?: string;
    companyId: string;
    code: string;
    taskRepetitionType: TaskRepetitionType;
    dataSchema: string;
    printOrientationLandscape: boolean;
    visibility: TaskVisibility;
    title: string;
    taskType: ITaskEditTypeItem;
    headerDataRow: ITaskDataRow;
    formDataRow: ITaskDataRow;
    headerTypeCol?: ITaskTypeCol;
    formTypeCol?: ITaskTypeCol;
    showPreviewWithoutCard?: boolean;
    onGetPrintHtml: (obj: any) => Promise<TaskTypePrintHtml>;
    onGetPrintHtmlDisableLoadIndicator?: boolean;
    onGetPrintHtmlFinally?: (result?: TaskTypePrintHtml) => void;
    onGetPrint: (obj: any) => Promise<string>;
    onHeaderItemClick?: (id: string) => void;
    onFormItemClick?: (id: string) => void;
}

export const TaskTypeDialogPreviewPrint = (props: ITaskTypeDialogPreviewPrintProps) => {
    const containerClassName = "task-form-designer-preview-print";
    const observerRef = useRef<MutationObserver>(null);
    const divRef = useRef<HTMLDivElement>(null);
    const [previewHtml, setPreviewHtml] = useState("");
    const [headerElements, setHeaderElements] = useState<Element[]>([]);
    const [formElements, setFormElements] = useState<Element[]>([]);
    const [mutationId, setMutationId] = useState("");

    const getSaveData = (): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("code", props.code);
        result.append("taskRepetitionType", props.taskRepetitionType);
        result.append("dataSchema", props.dataSchema);
        result.append("printOrientationLandscape", props.printOrientationLandscape);
        result.append("visibility", props.visibility);
        result.append("activeState", 1);
        result.append("taskHeaderDataCols", props.taskType.headerCols && props.taskType.headerCols.length
            ? JSON.stringify(props.headerDataRow.cols.map(i => new SaveTaskDataRowCol(i)))
            : null);
        result.append("taskDataRow", props.formDataRow
            ? JSON.stringify(SaveTaskDataRow.createSaveTaskDataRow(props.taskType.formCols, props.formDataRow, null))
            : null
        );
        return result;
    };

    const elementIsInHeader = (element: Element): boolean => {
        return element.classList.contains("header");
    };

    const getElementField = (element: Element): string => {
        const value = element.getAttribute("id");
        if (!value) return "";
        return value.substring(6);
    };

    const clickEvent = (event: any) => {
        const element = event.target.closest(".field");
        if (!element) return;
        const field = getElementField(element);
        if (!field) return;
        if (elementIsInHeader(element)) {
            props.onHeaderItemClick(field);
        } else {
            props.onFormItemClick(field);
        }
    };

    const setSelectedElement = (tempHeaderElements: Element[], tempFormElements: Element[]) => {
        for (const element of tempHeaderElements) {
            element.classList.remove("selected");
        }
        for (const element of tempFormElements) {
            element.classList.remove("selected");
        }
        if (props.headerTypeCol?.field) {
            for (const element of tempHeaderElements) {
                const field = getElementField(element);
                if (field === props.headerTypeCol.field) {
                    element.classList.add("selected");
                    break;
                }
            }
        }
        if (props.formTypeCol?.field) {
            for (const element of tempFormElements) {
                const field = getElementField(element);
                if (field === props.formTypeCol.field) {
                    element.classList.add("selected");
                    break;
                }
            }
        }
    };

    const attachClickEvents = () => {
        const elements = document.querySelectorAll(`.${containerClassName} .field`);
        const tempHeaderElements: Element[] = [];
        const tempFormElements: Element[] = [];
        elements.forEach((element) => {
            element.addEventListener("click", clickEvent);
            if (elementIsInHeader(element)) {
                tempHeaderElements.push(element);
            } else {
                tempFormElements.push(element);
            }
        });
        setHeaderElements(tempHeaderElements);
        setFormElements(tempFormElements);
        setSelectedElement(tempHeaderElements, tempFormElements);
    };

    const removeClickEvents = () => {
        const elements = document.querySelectorAll(`.${containerClassName} .field`);
        elements.forEach((element) => {
            element.removeEventListener("click", clickEvent);
        });
        setHeaderElements([]);
        setFormElements([]);
    };

    const loadPreviewData = async () => {
        //Call server
        const result = await AppUtils.callService(() => props.onGetPrintHtml(getSaveData().obj), false, props.onGetPrintHtmlDisableLoadIndicator, props.onGetPrintHtmlFinally);
        if (!result) return;
        removeClickEvents();
        setPreviewHtml(result.html);
        // window.setTimeout(() => {
        //     attachClickEvents();
        // }, 0);
    };
    
    const handleMutation = useCallback(() => {
        setMutationId(Base.getGuid());
    }, [previewHtml]);

    useEffect(() => {
        observerRef.current = new MutationObserver(handleMutation);
        observerRef.current.observe(divRef.current, {
            childList: true,
            attributes: true,
            characterData: true
        });

        return () => {
            observerRef.current.disconnect();
            removeClickEvents();
        };
    }, []);

    useEffect(() => {
        loadPreviewData();
    }, [props.companyId, props.dataSchema]); //, props.onGetPrint, props.onGetPrintHtml

    useEffect(() => {
        setSelectedElement(headerElements, formElements);
    }, [props.headerTypeCol?.field, props.formTypeCol?.field]);

    useEffect(() => {
        if (!mutationId) return;
        attachClickEvents();
    }, [mutationId]);

    const handlePrint = async () => {
        const fileName = await AppUtils.callService(() => props.onGetPrint(getSaveData().obj));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.PrintParameterDownloaded, fileName));
    };

    if (props.showPreviewWithoutCard) {
        return (
            <Box ref={divRef} className={containerClassName} dangerouslySetInnerHTML={{ __html: previewHtml }} />
        );
    }

    return (
        <DialogSectionCard
            headerTitle={props.title}
            contentPadding="16px 0 16px 0!important"
            buttons={
                <PrintButton onClick={handlePrint} size="small" variant="outlined">
                    {Translations.Print}
                </PrintButton>
            }
        >
            <Box
                /* eslint-disable quote-props */
                sx={{
                    position: "relative",
                    height: props.height ?? "calc(100vh - 350px)",
                    overflow: "auto",
                    paddingLeft: "0",
                    paddingRight: "0",
                    "& img": {
                        verticalAlign: "baseline"
                    }
                }}
                /* eslint-enable quote-props */
            >
                <Box ref={divRef} className={containerClassName} sx={{ paddingLeft: "16px", paddingRight: "16px" }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </Box>
        </DialogSectionCard>
    );
};
